import { useState } from 'react';
import {
  Combobox as HeadlessCombobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { twJoin } from 'tailwind-merge';
import { type VariantProps, tv } from 'tailwind-variants';
import { Spinner } from '../Spinner';
import { Stack } from '../Stack';
import { defaultInputClasses } from '../utils';
import { Label } from './Label';

const comboboxVariants = tv({
  slots: {
    wrapper: '',
    input: twJoin('w-full border-2 text-sm font-aribau', defaultInputClasses),
    menu: twJoin(
      'absolute z-20 w-full max-h-60 overflow-auto bg-white py-1 mt-1 shadow-lg',
      'focus:outline-none',
      'data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in',
    ),
    menuItem: twJoin(
      'group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900',
      'transition-all duration-100 data-[focus]:bg-primary-600 data-[focus]:text-white',
    ),
  },
  variants: {
    fullWidth: {
      true: {
        wrapper: 'w-full',
      },
    },
    align: {
      inherit: {
        input: '',
      },
      left: {
        input: 'text-left',
      },
      center: {
        input: 'text-center',
      },
      right: {
        input: 'text-right',
      },
    },
    invalid: {
      true: {
        input:
          'border-red-500 bg-red-100 focus:bg-red-50 focus:border-red-500 focus:ring-red-500/50',
      },
      false: {
        input: 'border-gray-100 bg-gray-100 focus:bg-gray-50 focus:border-gray-100',
      },
    },
  },
  defaultVariants: {
    align: 'center',
    fullWidth: true,
    invalid: false,
  },
});

type ComboboxVariants = VariantProps<typeof comboboxVariants>;

export interface ComboboxItem {
  id: string;
}

interface ComboboxProps<T extends ComboboxItem> extends ComboboxVariants {
  name: string;
  label?: string;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  items: T[];
  selected: T | null;
  onChange: (item: T | null) => void;
  onQueryChange: (query: string) => void;
  displayValue: (item: T | null) => string;
  renderItem: (item: T) => React.ReactNode;
  emptyMessage?: string;
  className?: string;
}

export function Combobox<T extends ComboboxItem>({
  name,
  label: labelText,
  placeholder,
  loading,
  disabled = false,
  items,
  selected,
  onChange,
  onQueryChange,
  displayValue,
  renderItem,
  emptyMessage = 'No results found',
  fullWidth,
  align,
  invalid,
  className,
}: ComboboxProps<T>): React.ReactElement {
  const [query, setQuery] = useState('');
  const { wrapper, input, menu, menuItem } = comboboxVariants();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    onQueryChange(value);
  };

  return (
    <Stack gap={2} className={wrapper({ fullWidth, className })}>
      {labelText && <Label htmlFor={name}>{labelText}</Label>}
      <HeadlessCombobox value={selected} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <Stack direction="row">
            <ComboboxInput
              id={name}
              className={input({ invalid, align })}
              displayValue={displayValue}
              onChange={handleInputChange}
              placeholder={placeholder}
              autoComplete="off"
            />
            {loading && <Spinner className="opacity-45 w-4 h-4 right-2 absolute" />}
          </Stack>

          <ComboboxOptions transition className={menu()}>
            {query.length >= 2 && items.length === 0 && !loading && (
              <div className="px-3 py-2 text-gray-500">{emptyMessage}</div>
            )}

            {items.map((item) => (
              <ComboboxOption key={item.id} value={item} className={menuItem()}>
                {renderItem(item)}

                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                  <CheckIcon aria-hidden="true" className="size-4" />
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </div>
      </HeadlessCombobox>
    </Stack>
  );
}
